import { UploadedFileEntry } from '@interfaces/mfe/mfe-document-upload.interface';
import { ReceiptRow } from '@interfaces/models/receipt.interface';
import {
  SettlementFundAoAEnum,
  SettlementFundMap,
} from '@interfaces/settlement-fund/settlement-fund.enum';
import { TaxUploadOptionKey, taxUploadOptionsKeys, taxUploadOptionsMap } from '@shared/models';
import { ReceiptModal } from '@shared/models/receipt-modal';
import { ReviewContent } from '@shared/services/content/content-jsons/review-content';
import { ILetterUploadState } from '@store/reducers/letter-upload/letter-upload.reducer';

// Derives a lowercase file extension from a WLD mimeType ("PNG"/"PDF" or "image/png"/"application/pdf").
export function getExtensionFromMimeType(mimeType: string | null | undefined): string {
  if (!mimeType) {
    return '';
  }
  const lower = mimeType.toLowerCase().trim();
  const subtype = lower.includes('/') ? lower.slice(lower.lastIndexOf('/') + 1) : lower;
  return subtype === 'jpeg' ? 'jpg' : subtype;
}

// Appends the mimeType extension to a WLD documentName when missing (avoids duplicates like "will.pdf.pdf").
export function buildWldDocumentDisplayName(documentName: string, mimeType: string): string {
  const extension = getExtensionFromMimeType(mimeType);
  if (!extension || documentName.toLowerCase().endsWith(`.${extension}`)) {
    return documentName;
  }
  return `${documentName}.${extension}`;
}

// Builds the "Account approval letter" receipt row, or null when no letter is present.
// Shared by the employment receipt builders to keep their cyclomatic complexity within limits.
export function buildAccountApprovalLetterRow(
  letterUploadState: ILetterUploadState,
  wldLetterDocuments: UploadedFileEntry[] = [],
): ReceiptRow | null {
  const wldLetterFileNames = wldLetterDocuments.map((document) =>
    buildWldDocumentDisplayName(document.documentName, document.mimeType),
  );
  const letterFileName = letterUploadState.filesMetaData[0]?.fileName;
  if (!letterUploadState.vgHasLetter && !letterFileName && wldLetterFileNames.length === 0) {
    return null;
  }

  let letterValues: string[];
  if (letterUploadState.vgHasLetter) {
    letterValues = ['On file'];
  } else if (wldLetterFileNames.length > 0) {
    letterValues = wldLetterFileNames;
  } else {
    letterValues = [letterFileName];
  }

  return {
    label: 'Account approval letter',
    values: letterValues,
    evidenceID: 'employmentApprovalLetter',
  };
}

// Export for testing edge cases
export const splitWithNewline = (str: string) => {
  const mid = Math.floor(str.length / 2);
  // Try to split at the nearest space BEFORE the midpoint
  let breakIndex = str.lastIndexOf(' ', mid);
  // If no space found before, find the nearest AFTER
  if (breakIndex === -1) {
    breakIndex = str.indexOf(' ', mid);
  }
  // If there are still no spaces at all, split in half
  if (breakIndex === -1) {
    breakIndex = mid;
  }
  return str.slice(0, breakIndex) + '<br>' + str.slice(breakIndex + 1);
};

export function buildSettlementFundRow(
  shouldShowMFESection: boolean,
  settlementFundChoice: SettlementFundAoAEnum | null | undefined,
  modalId?: string,
): ReceiptRow {
  return {
    label: 'Settlement fund',
    values: [SettlementFundMap[settlementFundChoice ?? SettlementFundAoAEnum.FED]].filter(
      (value): value is string => value !== undefined,
    ),
    modal: shouldShowMFESection ? undefined : modalId || ReceiptModal.SETTLEMENTFUND,
    evidenceID: 'settlementFund',
    additionalText: shouldShowMFESection
      ? ReviewContent.settlementFundDescription
      : ReviewContent.fundInvestWarning,
  };
}

export function buildL8ITWDocumentsRow(
  L8ITWSelectedKey: TaxUploadOptionKey,
  additionalText: string,
  formName: string,
): ReceiptRow {
  return {
    label: formName,
    values: [splitWithNewline(taxUploadOptionsMap[L8ITWSelectedKey])],
    additionalText: additionalText,
    evidenceID: 'L8ITWDocuments',
  };
}
